import { useState, useEffect, useCallback } from "react";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import Modal from "../../components/ui/Modal";
import { adminApi } from "../../api/admin";
const EMPTY_THEME = { logo_url: "", app_name: "FastBlade", theme_color: "#3b82f6" };
const EMPTY_CREATE = { name: "", slug: "", theme: { ...EMPTY_THEME } };

// ── Helpers ───────────────────────────────────────────────────────────────────

function slugify(str) {
  return str.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
}

function Field({ label, value, onChange, type = "text", placeholder, disabled }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-500 block mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-bh-400 disabled:bg-gray-50 disabled:text-gray-400"
      />
    </label>
  );
}

function ThemeFields({ theme, onChange }) {
  const set = (k) => (e) => onChange({ ...theme, [k]: e.target.value });
  return (
    <div className="space-y-3 pt-2 border-t border-gray-100">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Theme</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="App name" value={theme.app_name ?? ""} onChange={set("app_name")} placeholder="FastBlade" />
        <Field label="Logo URL" value={theme.logo_url ?? ""} onChange={set("logo_url")} placeholder="/logos/my-logo.png" />
      </div>
      <label className="block">
        <span className="text-xs font-medium text-gray-500 block mb-1">Brand color — full palette generated from this</span>
        <div className="flex gap-2 items-center">
          <input
            type="color"
            value={theme.theme_color ?? "#3b82f6"}
            onChange={set("theme_color")}
            className="h-9 w-10 rounded border border-gray-300 cursor-pointer p-0.5"
          />
          <input
            type="text"
            value={theme.theme_color ?? ""}
            onChange={set("theme_color")}
            placeholder="#3b82f6"
            className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-bh-400 font-mono"
          />
        </div>
      </label>
      {theme.logo_url && (
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <img src={theme.logo_url} alt="logo preview" className="h-6 w-auto object-contain"
               onError={(e) => { e.target.style.display = "none"; }} />
          <span>Logo preview</span>
        </div>
      )}
    </div>
  );
}

// ── Create modal ──────────────────────────────────────────────────────────────

function CreateEntityModal({ open, onClose, onCreated }) {
  const [form, setForm]   = useState(EMPTY_CREATE);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const [slugEdited, setSlugEdited] = useState(false);

  useEffect(() => {
    if (open) { setForm(EMPTY_CREATE); setError(null); setSlugEdited(false); }
  }, [open]);

  const setName = (e) => {
    const name = e.target.value;
    setForm((f) => ({
      ...f,
      name,
      slug: slugEdited ? f.slug : slugify(name),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const { data } = await adminApi.createEntity(form);
      onCreated(data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to create entity");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Entity">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name *" value={form.name} onChange={setName} required />
          <label className="block">
            <span className="text-xs font-medium text-gray-500 block mb-1">Slug *</span>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => { setSlugEdited(true); setForm((f) => ({ ...f, slug: slugify(e.target.value) })); }}
              required
              placeholder="my-entity"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-bh-400 font-mono"
            />
            <p className="text-xs text-gray-400 mt-0.5">Used as identifier — cannot be changed later</p>
          </label>
        </div>
        <ThemeFields theme={form.theme} onChange={(t) => setForm((f) => ({ ...f, theme: t }))} />
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={saving}
            className="px-4 py-2 bg-bh-600 text-white rounded-lg text-sm font-medium hover:bg-bh-700 disabled:opacity-50">
            {saving ? "Creating…" : "Create entity"}
          </button>
          <button type="button" onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Edit modal ────────────────────────────────────────────────────────────────

function EditEntityModal({ entity, open, onClose, onUpdated }) {
  const [form, setForm]   = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    if (open && entity) {
      setForm({ name: entity.name, theme: { ...EMPTY_THEME, ...(entity.theme || {}) } });
      setError(null);
    }
  }, [open, entity]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const { data } = await adminApi.updateEntity(entity.id, form);
      onUpdated(data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!entity) return null;

  return (
    <Modal open={open} onClose={onClose} title={`Edit — ${entity.name}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name *" value={form.name ?? ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
          <Field label="Slug" value={entity.slug} disabled />
        </div>
        <ThemeFields theme={form.theme ?? EMPTY_THEME} onChange={(t) => setForm((f) => ({ ...f, theme: t }))} />
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={saving}
            className="px-4 py-2 bg-bh-600 text-white rounded-lg text-sm font-medium hover:bg-bh-700 disabled:opacity-50">
            {saving ? "Saving…" : "Save changes"}
          </button>
          <button type="button" onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AdminEntitiesPage() {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.listEntities();
      setEntities(data);
    } catch {
      setError("Failed to load entities");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (entity) => {
    if (!window.confirm(`Delete entity "${entity.name}"? This cannot be undone.`)) return;
    try {
      await adminApi.deleteEntity(entity.id);
      setEntities((prev) => prev.filter((e) => e.id !== entity.id));
    } catch (err) {
      alert(err?.response?.data?.error || "Delete failed");
    }
  };

  return (
    <div className="p-6 max-w-5xl space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Entity Management</h1>
          <p className="text-sm text-gray-500 mt-0.5">{entities.length} entit{entities.length !== 1 ? "ies" : "y"} configured</p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-bh-600 text-white rounded-lg text-sm font-medium hover:bg-bh-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          Add entity
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Entity</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Slug</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Brand color</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Users</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Projects</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {entities.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">No entities yet.</td>
                </tr>
              )}
              {entities.map((e) => (
                <tr key={e.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {e.theme?.logo_url && (
                        <img src={e.theme.logo_url} alt={e.name} className="h-6 w-auto object-contain"
                             onError={(el) => { el.target.style.display = "none"; }} />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{e.name}</p>
                        {e.is_default && (
                          <span className="text-xs text-bh-600 font-medium">Default</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{e.slug}</td>
                  <td className="px-4 py-3">
                    {e.theme?.theme_color ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded border border-gray-200 shrink-0" style={{ background: e.theme.theme_color }} />
                        <span className="font-mono text-xs text-gray-500">{e.theme.theme_color}</span>
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{e.user_count ?? 0}</td>
                  <td className="px-4 py-3 text-gray-600">{e.project_count ?? 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => setEditTarget(e)}
                        className="p-1.5 rounded text-gray-400 hover:text-bh-600 hover:bg-bh-50 transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(e)}
                        disabled={e.is_default || (e.user_count > 0) || (e.project_count > 0)}
                        className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title={
                          e.is_default ? "Cannot delete the default entity"
                          : (e.user_count || e.project_count) ? "Reassign users and projects first"
                          : "Delete"
                        }
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateEntityModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(e) => { setEntities((prev) => [...prev, e]); }} />
      <EditEntityModal entity={editTarget} open={!!editTarget} onClose={() => setEditTarget(null)} onUpdated={(e) => setEntities((prev) => prev.map((x) => (x.id === e.id ? e : x)))} />
    </div>
  );
}
